import {
  booleanAttribute,
  computed,
  Directive,
  ElementRef,
  HostListener,
  input,
} from '@angular/core';

const stringToNumber = (value: string | number): number => {
  const result = Number(String(value || '0').replace(/,/g, ''));
  return isNaN(result) ? 0 : result;
};

@Directive({
  selector: '[inputNumber]',
})
export class InputNumberDirective {
  private onChange = (_: any) => {};
  private onTouched = () => {};
  private el: HTMLInputElement;

  // 통화 기호 (₩, $, 등)
  currencySymbol = input<string>('');
  inputNumber = input<boolean, string>(false, { transform: booleanAttribute });

  // ✅ 최대값 설정 (null이면 제한 없음)
  _max = input<number | null>(null, { alias: 'max' });
  max = computed(() =>
    this._max() ? stringToNumber(this._max()!) : undefined
  );

  withCommas = input<boolean, string>(false, { transform: booleanAttribute });

  constructor(private elementRef: ElementRef) {
    this.el = this.elementRef.nativeElement;
  }

  @HostListener('input', ['$event'])
  onInput(event: any): void {
    if (!this.inputNumber()) return;

    const rawValue = this.el.value;

    // ✅ 숫자, 음수기호(-), 소수점(.)만 허용
    let numeric = rawValue.replace(/[^0-9.-]/g, '');

    // ✅ 선행 0 제거 (단, "0.x"는 유지)
    numeric = numeric.replace(/^(-?)0+(?!\.)/, '$1');

    // ✅ 입력 중인 상태 예외 처리 (삭제 중일 수도 있음)
    if (
      numeric === '' ||
      numeric === '-' ||
      numeric === '.' ||
      numeric === '-.'
    ) {
      this.el.value = numeric;
      this.onChange(null);
      return;
    }

    let numericValue = parseFloat(numeric);

    // ✅ NaN 방지
    if (isNaN(numericValue)) {
      this.el.value = '';
      this.onChange(null);
      return;
    }

    // ✅ max 적용
    if (this.max() != null && numericValue > this.max()!) {
      numericValue = this.max()!;
    }

    const absValue = Math.abs(numericValue).toString();
    const [integerPart, decimalPart] = absValue.split('.');
    const formattedInteger = this.formatWithCommas(integerPart || '0');

    let formatted = formattedInteger;
    if (decimalPart !== undefined) {
      formatted += '.' + decimalPart.slice(0, 2); // 소수점 2자리 제한
    }

    if (numericValue < 0) {
      formatted = '-' + formatted;
    }

    if (this.currencySymbol()) {
      formatted = this.currencySymbol() + formatted;
    }

    this.el.value = formatted;
    this.onChange(numericValue);
  }

  @HostListener('focus')
  onFocus(): void {
    if (!this.inputNumber()) return;

    // ✅ 숫자 및 부호만 남김
    const numeric = this.el.value.replace(/[^0-9.-]/g, '');
    this.el.value = numeric;
  }

  @HostListener('blur')
  onBlur(): void {
    if (!this.inputNumber()) return;

    this.onInput(null); // 포맷팅 적용
    this.onTouched();
  }

  writeValue(value: any): void {
    if (!this.inputNumber()) return;

    if (value == null || value === '' || isNaN(value)) {
      this.el.value = '';
      return;
    }

    if (this.max() != null && value > this.max()!) {
      value = this.max()!;
    }

    let formatted = this.formatWithCommas(Math.abs(value).toString());
    if (value < 0) formatted = '-' + formatted;
    if (this.currencySymbol()) {
      formatted = this.currencySymbol() + formatted;
    }

    this.el.value = formatted;
  }

  registerOnChange(fn: any): void {
    if (!this.inputNumber()) return;

    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    if (!this.inputNumber()) return;

    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (!this.inputNumber()) return;

    this.el.disabled = isDisabled;
  }

  private formatWithCommas(value: string): string {
    if (!this.withCommas()) return value;

    // ❌ 숫자가 0인데도 false로 처리돼서 빈 문자열 반환 중
    if (!this.inputNumber()) return '';

    if (!value || value === '') return '0'; // ✅ 여기 추가!

    const [integer, decimal] = value.split('.');
    const result = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decimal ? `${result}.${decimal}` : result;
  }
}
